import { activeRightStack } from '@/app/store';
import { requestCall } from '@/app/store/commonSlice';
import { closeSidePanel, updateSidePanel } from '@/app/store/navigationSlice';
import { hidePopup, showPopup } from '@/components/PopupContainer/popupService';
import MeetPopup from '@/features/calendar/ui/MeetPopup/MeetPopup';
import { useUserDetails } from '@/features/users';
import { i18n } from '@/localization/i18n';
import { useLanguage } from '@/localization/useLanguage';
import { getUserIdFromJid, showInfoToast } from '@/shared/utils';
import { blockOfflineAction } from '@/shared/utils/network';
import { showErrorToast } from '@/shared/utils/toastMessage';
import { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useChatFeature } from '../chatsRegistry';
import { useAmIParticipant } from '../model/groupParticipants';
import { isActive } from '../sub-features/audio-recorder/utils/recorderSessions';
import GroupCallSelectionPopup from '../ui/Conversation/GroupCallSelectionPopup/GroupCallSelectionPopup';
import { isGroupChat } from '../utils';
import { useChatRequest } from './useChatRequest';

const EMPTY_ARRAY = [];

/**
 * ViewModel for ChatHeader.
 * Owns: contact info panel, call triggers, group call popup state, feature gates.
 *
 * Cross-feature calls: dispatches `requestCall` to store/commonSlice only.
 * The calls feature listens to state.common.pendingCall independently.
 */
export const useChatHeader = activeChatJid => {
   const { t } = useLanguage();
   const dispatch = useDispatch();
   const ongoingCallParticipants = useSelector(state => state.callkitData?.ongoingCallParticipants || EMPTY_ARRAY);
   const isCallActive = ongoingCallParticipants.length > 0;
   const chatId = getUserIdFromJid(activeChatJid);
   const { isBlockedByMe, isDeleted } = useUserDetails(chatId, ['isBlockedByMe', 'isDeleted']);
   const isGroup = isGroupChat(activeChatJid);
   const amIParticipant = useAmIParticipant(chatId);

   // Server + dev feature gates
   const isChatInfoEnabled = useChatFeature('chat-info');
   const isLastSeenEnabled = useChatFeature('last-seen');
   const isOneToOneCallEnabled = useSelector(state => state.features.isOneToOneCallEnabled ?? true);
   const isGroupCallEnabled = useSelector(state => state.features.isGroupCallEnabled ?? true);
   const { isFeatureGated, isReceiver } = useChatRequest(activeChatJid);

   const handleOpenContactInfo = useCallback(() => {
      if (isChatInfoEnabled && (!isFeatureGated || isReceiver)) {
         dispatch(
            updateSidePanel({
               id: 'chat-info',
               isOpen: true,
            }),
         );
      }
   }, [isChatInfoEnabled, isFeatureGated, isReceiver, dispatch]);

   const handleOneToOneCall = useCallback(
      type => {
         if (blockOfflineAction()) return;
         if (isCallActive) {
            showInfoToast(i18n.t('calls.ALREADY_IN_A_CALL', { defaultValue: 'You are already in call' }));
            return;
         } else if (isActive(activeChatJid)) {
            showErrorToast(t('toast.CANNOT_START_CALL_WHILE_RECORDING'));
            return;
         }
         dispatch(requestCall({ userId: chatId, callType: type, isGroup: false }));
      },
      [dispatch, chatId, isCallActive, activeChatJid, t],
   );

   const handleGroupCallClick = useCallback(
      type => {
         if (blockOfflineAction()) return;
         if (isCallActive) {
            showInfoToast(i18n.t('calls.ALREADY_IN_A_CALL', { defaultValue: 'You are already in call' }));
            return;
         } else if (isActive(activeChatJid)) {
            showErrorToast(t('toast.CANNOT_START_CALL_WHILE_RECORDING'));
            return;
         }
         showPopup({
            id: 'groupCallSelectionModal',
            visible: true,
            className: 'groupCallSelectionPopup',
            title: i18n.t('chat.GROUP_CALL_TITLE'),
            body: (
               <GroupCallSelectionPopup
                  activeChatJid={activeChatJid}
                  callType={type}
                  onClose={() => hidePopup('groupCallSelectionModal')}
               />
            ),
            onRequestClose: () => hidePopup('groupCallSelectionModal'),
         });
      },
      [activeChatJid, isCallActive, t],
   );

   const handleBack = useCallback(() => {
      dispatch(activeRightStack({ activeStack: '', activeChatId: '', activeChatJid: '' }));
      dispatch(closeSidePanel());
   }, [dispatch]);

   const handleOpenJoinMeeting = useCallback(() => {
      dispatch(activeRightStack({ activeStack: 'joinMeeting', activeChatId: '' }));
   }, [dispatch]);

   const isSidePanelOpen = useSelector(state => state.navigationData.sidePanel.isOpen);

   return {
      chatId,
      isGroup,
      isSidePanelOpen,
      isLastSeenEnabled,
      isCallActive,
      isBlockedByMe,
      showCalls: isGroup
         ? isGroupCallEnabled && amIParticipant && !isDeleted
         : isOneToOneCallEnabled && !isFeatureGated && !isBlockedByMe && !isDeleted,
      // Handlers
      handleOpenContactInfo,
      handleOneToOneCall,
      handleGroupCallClick,
      handleBack,
      handleOpenJoinMeeting,
      handleScheduleMeeting: (type = 'schedule') => {
         // Use the global showPopup for quick actions from header
         showPopup({
            id: 'meetPopupGlobal',
            visible: true,
            hideHeader: true,
            body: (
               <MeetPopup
                  isOpen={true}
                  onClose={() => hidePopup('meetPopupGlobal')}
                  onScheduleToggle={() => {}}
                  meetingType={type}
               />
            ),
         });
      },
   };
};
